<template>
  <div>
    <helpers-section-header class="mt-6 mb-2">WhaleMl Signals</helpers-section-header>
    

    <helpers-section-sub-header class="mt-6">Signal Evaluation Window</helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      How many days after a score is first published to continue treating it as an active buy
      signal. Since scores are refreshed weekly, setting this to <strong>7</strong> keeps each
      signal active until the next weekly update. Set higher to hold signals across multiple
      weekly updates.
    </p>
    <v-row>
      <v-col cols="12">
        <v-text-field
          v-model="criteria.number_of_days_to_evaluate"
          label="Days to keep signal active"
          suffix="days"
          variant="outlined"
          density="compact"
          hint="7 = active until the next weekly score update"
          persistent-hint
          style="max-width: 250px"
        />
      </v-col>
    </v-row>

    <helpers-section-sub-header class="mt-6">Overcrowding Risk Filter</helpers-section-sub-header>
    <p class="text-body-2 mb-2">
      Filter signals using the <strong>Overcrowding Risk Scores</strong>, which measure how vulnerable
      each stock is to an above-average correction during a black-swan or sentiment-shift event.
      All scores are <strong>0–100 percentile ranks</strong> within the stock universe — a higher
      score means more risk (except Resilience, where higher is better). Stocks without overcrowding
      data are always allowed through.
    </p>
    <v-alert type="info" variant="tonal" density="compact" class="mb-3">
      The options component of the overcrowding score is only available from <strong>October 2021</strong>
      onward. Backtests starting before that date will use a score computed without options data for
      earlier periods.
    </v-alert>
    <v-checkbox
      v-model="criteria.overcrowding_filter_enabled"
      label="Enable Overcrowding Risk Filter"
      hide-details
      density="compact"
      class="mb-4"
    />

    <v-row v-if="criteria.overcrowding_filter_enabled">
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.min_composite_score"
          label="Min Composite Score"
          type="number"
          min="0"
          max="100"
          step="1"
          variant="outlined"
          density="compact"
          hint="Overall overcrowding risk floor. Only include stocks at or above this percentile (e.g. 20 = exclude least-crowded 20%). 0 = no minimum."
          persistent-hint
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_composite_score"
          label="Max Composite Score"
          type="number"
          min="0"
          max="100"
          step="1"
          variant="outlined"
          density="compact"
          hint="Overall overcrowding risk ceiling. Avoid stocks above this percentile (e.g. 80 = exclude top 20% riskiest)."
          persistent-hint
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.min_resilience_score"
          label="Min Resilience Score"
          type="number"
          min="0"
          max="100"
          step="1"
          variant="outlined"
          density="compact"
          hint="Resilience to corrections (low beta, defensive sector, put-heavy). Higher = more defensive. 0 = no minimum."
          persistent-hint
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_institutional_score"
          label="Max Institutional Score"
          type="number"
          min="0"
          max="100"
          step="1"
          variant="outlined"
          density="compact"
          hint="13F filer concentration &amp; accumulation trend. 100 = no limit."
          persistent-hint
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_technical_score"
          label="Max Technical Score"
          type="number"
          min="0"
          max="100"
          step="1"
          variant="outlined"
          density="compact"
          hint="Price extension above moving averages &amp; proximity to 52-week high. 100 = no limit."
          persistent-hint
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_options_score"
          label="Max Options Score"
          type="number"
          min="0"
          max="100"
          step="1"
          variant="outlined"
          density="compact"
          hint="Call/put OI imbalance, IV skew, speculative OTM call bias. 100 = no limit."
          persistent-hint
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_beta_score"
          label="Max Beta Score"
          type="number"
          min="0"
          max="100"
          step="1"
          variant="outlined"
          density="compact"
          hint="Market sensitivity and realized volatility. 100 = no limit."
          persistent-hint
        />
      </v-col>
      <v-col cols="12" sm="6" md="4" xl="3">
        <v-text-field
          v-model="criteria.max_exit_pressure_score"
          label="Max Exit Pressure Score"
          type="number"
          min="0"
          max="100"
          step="1"
          variant="outlined"
          density="compact"
          hint="Insider selling, analyst estimate cuts, equity issuances. 100 = no limit."
          persistent-hint
        />
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapWritableState } from 'pinia'
import { useAppStore } from '@/store'
export default {
  computed: {
    ...mapWritableState(useAppStore, ['whaleTraderCriteria']),
    criteria() {
      return this.whaleTraderCriteria.signals.whaleml_signal
    }
  }
}
</script>
